import React from "react";

// ─── ActivityTraceSVG ──────────────────────────────────────────────────────────
// Renders a privacy-safe activity trace: `points` are already normalized to a
// unit box (0..1, y up) server-side — no absolute GPS location is ever passed
// in. Ridgeline aesthetic: a single stroked polyline, no fill, no background.
// Reused by PostCard's activity embed and (later) PLT-3.

export interface ActivityTraceSVGProps {
  points: Array<[number, number]>;
  height?: number;
  stroke?: string;
  strokeWidth?: number;
}

export function ActivityTraceSVG({
  points,
  height = 64,
  stroke = "var(--sky-400)",
  strokeWidth = 2,
}: ActivityTraceSVGProps) {
  if (!points || points.length < 2) return null;

  // Points are normalized server-side into a unit box that PRESERVES aspect:
  // the larger dimension spans 0..1, the smaller spans less. A fixed 0 0 1 1
  // viewBox with preserveAspectRatio="none" would stretch non-square traces
  // back out, destroying that shape — so compute the actual bounding box of
  // the drawn points and let the SVG letterbox/pillarbox instead.
  let maxX = 0;
  let maxY = 0;
  for (const [x, y] of points) {
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  // Guard degenerate spans (all points on a horizontal/vertical line) so the
  // viewBox never gets a 0 width/height.
  const EPS = 0.02;
  const w = Math.max(maxX, EPS);
  const h = Math.max(maxY, EPS);

  // SVG y grows down while points are y-up (math convention), so flip within
  // the box height. Add a small inset so the stroke never clips at the edge.
  const inset = 0.04;
  const viewW = w + inset * 2;
  const viewH = h + inset * 2;
  const coords = points
    .map(([x, y]) => {
      const sx = inset + x;
      const sy = inset + (h - y);
      return `${sx.toFixed(4)},${sy.toFixed(4)}`;
    })
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${viewW.toFixed(4)} ${viewH.toFixed(4)}`}
      preserveAspectRatio="xMidYMid meet"
      width="100%"
      height={height}
      style={{ display: "block", background: "transparent" }}
      aria-hidden="true"
    >
      <polyline
        points={coords}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
